import { useState, useEffect, useMemo, useCallback } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { EmpresaSidebar } from "@/components/empresa/EmpresaSidebar";
import { useCompanyAccess } from "@/hooks/useCompanyAccess";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Loader2, Briefcase, FileText, Search, MapPin, Building2, Plus, Edit, RefreshCw, CheckCircle, Wallet } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { format, subDays, startOfDay, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";

const JOBS_PER_PAGE = 20;

type Job = {
  id: string;
  title: string;
  city: string | null;
  state: string | null;
  job_type: string[];
  is_active: boolean;
  status: string | null;
  created_at: string;
  applications_count?: number;
};

type Application = {
  applied_at: string;
  job_id: string;
  status: string;
};

const EmpresaDashboardPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { loading: accessLoading, companyId } = useCompanyAccess(true);
  
  // Metrics states
  const [applications, setApplications] = useState<Application[]>([]);
  const [activeJobsCount, setActiveJobsCount] = useState(0);
  const [credits, setCredits] = useState(0);
  const [cities, setCities] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Pagination states
  const [paginatedJobs, setPaginatedJobs] = useState<Job[]>([]);
  const [totalJobsCount, setTotalJobsCount] = useState(0);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Other states
  const [reposting, setReposting] = useState<string | null>(null);
  const [period, setPeriod] = useState("30");
  const [filterType, setFilterType] = useState("all");
  const [filterCity, setFilterCity] = useState("all");
  const [filterStatus, setFilterStatus] = useState("ativa");
  const [searchTerm, setSearchTerm] = useState("");

  const totalPages = Math.ceil(totalJobsCount / JOBS_PER_PAGE);

  // Fetch metrics (once on load)
  const fetchMetrics = useCallback(async () => {
    if (!companyId) return;
    
    setLoading(true);
    
    // Fetch active jobs count and credits in parallel
    const [jobsResult, profileResult] = await Promise.all([
      supabase
        .from("jobs")
        .select("id", { count: "exact", head: true })
        .eq("company_id", companyId)
        .eq("is_active", true),
      supabase
        .from("company_profiles")
        .select("credits")
        .eq("id", companyId)
        .single()
    ]);
    
    setActiveJobsCount(jobsResult.count || 0);
    setCredits(profileResult.data?.credits || 0);
    
    // Fetch all job IDs for this company
    const { data: jobIds } = await supabase
      .from("jobs")
      .select("id")
      .eq("company_id", companyId);
    
    if (jobIds && jobIds.length > 0) {
      const ids = jobIds.map(j => j.id);
      const { data: appsData } = await supabase
        .from("applications")
        .select("applied_at, job_id, status")
        .in("job_id", ids);
      
      if (appsData) {
        setApplications(appsData);
      }
    }
    
    // Fetch unique cities for filter
    const { data: citiesData } = await supabase
      .from("jobs")
      .select("city")
      .eq("company_id", companyId)
      .not("city", "is", null);
    
    if (citiesData) {
      const uniqueCities = [...new Set(citiesData.map(j => j.city))].filter(Boolean).sort();
      setCities(uniqueCities as string[]);
    }
    
    setLoading(false);
  }, [companyId]);

  // Fetch paginated jobs (when filters/page change)
  const fetchJobs = useCallback(async () => {
    if (!companyId) return;
    
    setJobsLoading(true);
    
    const from = (currentPage - 1) * JOBS_PER_PAGE;
    const to = from + JOBS_PER_PAGE - 1;
    
    let queryBuilder = supabase
      .from("jobs")
      .select("id, title, city, state, job_type, is_active, status, created_at", { count: 'exact' })
      .eq("company_id", companyId);
    
    // Apply SQL filters
    if (filterStatus !== "all") {
      queryBuilder = queryBuilder.eq("status", filterStatus);
    }
    
    if (filterType !== "all") {
      queryBuilder = queryBuilder.contains("job_type", [filterType]);
    }
    
    if (filterCity !== "all") {
      queryBuilder = queryBuilder.eq("city", filterCity);
    }
    
    if (searchTerm) {
      queryBuilder = queryBuilder.ilike("title", `%${searchTerm}%`);
    }
    
    const { data: jobsData, count } = await queryBuilder
      .order("created_at", { ascending: false })
      .range(from, to);
    
    setTotalJobsCount(count || 0);
    
    if (jobsData) {
      // Add applications count for each job
      const jobsWithCount = jobsData.map(job => ({
        ...job,
        applications_count: applications.filter(a => a.job_id === job.id).length
      }));
      setPaginatedJobs(jobsWithCount);
    }
    
    setJobsLoading(false);
  }, [companyId, currentPage, filterStatus, filterType, filterCity, searchTerm, applications]);

  // Fetch metrics on load
  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Fetch jobs when filters/page/applications change
  useEffect(() => {
    if (!loading) {
      fetchJobs();
    }
  }, [fetchJobs, loading]);

  // Metrics calculations
  const pendingEvaluations = useMemo(
    () => applications.filter((a) => a.status === "enviado").length,
    [applications]
  );

  // Chart data based on period
  const chartData = useMemo(() => {
    const days = parseInt(period);
    const startDate = days === 0 ? startOfDay(new Date()) : subDays(new Date(), days);
    
    const filteredApps = applications.filter(
      (a) => parseISO(a.applied_at) >= startDate
    );

    // Group by date
    const grouped: Record<string, number> = {};
    
    let fullData: { name: string; candidaturas: number }[];
    
    if (days === 0) {
      // Today - group by hour
      for (let i = 0; i < 24; i++) {
        grouped[`${i}h`] = 0;
      }
      filteredApps.forEach((app) => {
        const hour = parseISO(app.applied_at).getHours();
        grouped[`${hour}h`]++;
      });
      fullData = Object.entries(grouped).map(([name, candidaturas]) => ({ name, candidaturas }));
    } else {
      // Group by day
      for (let i = days; i >= 0; i--) {
        const date = subDays(new Date(), i);
        const key = format(date, "dd/MM");
        grouped[key] = 0;
      }
      filteredApps.forEach((app) => {
        const key = format(parseISO(app.applied_at), "dd/MM");
        if (grouped[key] !== undefined) {
          grouped[key]++;
        }
      });
      fullData = Object.entries(grouped).map(([name, candidaturas]) => ({ name, candidaturas }));
    }

    // No mobile, mostrar apenas 3 pontos equidistantes
    if (isMobile && fullData.length > 3) {
      const first = fullData[0];
      const middle = fullData[Math.floor(fullData.length / 2)];
      const last = fullData[fullData.length - 1];
      return [first, middle, last];
    }

    return fullData;
  }, [applications, period, isMobile]);

  const jobTypeLabels: Record<string, string> = {
    presencial: "Presencial",
    híbrido: "Híbrido",
    remoto: "Remoto",
  };

  // Pagination functions
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const generatePageNumbers = (): (number | "ellipsis")[] => {
    const pages: (number | "ellipsis")[] = [];
    
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push("ellipsis");
      }
      
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push("ellipsis");
      }
      
      pages.push(totalPages);
    }
    
    return pages;
  };

  const handleMarkAsFilled = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ status: 'preenchida', is_active: false })
        .eq('id', jobId);
      
      if (error) throw error;
      
      // Refresh jobs after update
      fetchJobs();
      setActiveJobsCount(prev => prev - 1);
      
      toast({ title: "Vaga marcada como preenchida!" });
    } catch (error: any) {
      console.error('Erro ao marcar vaga:', error);
      toast({ 
        title: "Erro ao atualizar status da vaga", 
        variant: "destructive" 
      });
    }
  };

  const handleRepostJob = async (job: Job) => {
    setReposting(job.id);
    try {
      // 1. Fetch full original job data
      const { data: originalJob, error: fetchError } = await supabase
        .from("jobs")
        .select("*")
        .eq("id", job.id)
        .single();

      if (fetchError || !originalJob) throw new Error("Erro ao buscar vaga original");

      // 2. Create new job with same data
      const { data: newJob, error: insertError } = await supabase
        .from("jobs")
        .insert({
          company_id: companyId,
          title: originalJob.title,
          description: originalJob.description,
          job_type: originalJob.job_type,
          seniority_level: originalJob.seniority_level,
          area: originalJob.area,
          state: originalJob.state,
          city: originalJob.city,
          neighborhood: originalJob.neighborhood,
          work_schedule: originalJob.work_schedule,
          contract_type: originalJob.contract_type,
          workload: originalJob.workload,
          salary_range_start: originalJob.salary_range_start,
          salary_range_end: originalJob.salary_range_end,
          benefits: originalJob.benefits,
          status: 'ativa',
          is_active: true,
        })
        .select("id")
        .single();

      if (insertError || !newJob) throw new Error("Erro ao criar nova vaga");

      // 3. Fetch original job questions
      const { data: originalQuestions } = await supabase
        .from("job_questions")
        .select("*")
        .eq("job_id", job.id)
        .order("order_index", { ascending: true });

      // 4. Duplicate questions to new job
      if (originalQuestions && originalQuestions.length > 0) {
        const newQuestions = originalQuestions.map((q) => ({
          job_id: newJob.id,
          question_text: q.question_text,
          question_type: q.question_type,
          options: q.options,
          is_required: q.is_required,
          order_index: q.order_index,
        }));

        await supabase.from("job_questions").insert(newQuestions);
      }

      toast({ 
        title: "Vaga duplicada!", 
        description: "Edite as informações e publique a nova vaga." 
      });
      
      // 5. Navigate to edit page
      navigate(`/empresa/vaga/${newJob.id}/editar`);

    } catch (error: any) {
      toast({ 
        title: "Erro ao repostar vaga", 
        description: error.message, 
        variant: "destructive" 
      });
    } finally {
      setReposting(null);
    }
  };

  if (accessLoading || loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center bg-muted/30">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-1 flex">
        <EmpresaSidebar />
        <main className="flex-1 p-4 md:p-6 bg-muted/30 overflow-auto">
          <div className="mobile-container max-w-6xl md:mx-auto space-y-6 pt-14 md:pt-0">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h1 className="text-2xl md:text-3xl font-display font-bold">Dashboard</h1>
              <Button onClick={() => navigate("/empresa/criar-vaga")} className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Criar Nova Vaga
              </Button>
            </div>

            {/* Metrics Cards - Three cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Active Jobs */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Vagas Ativas
                  </CardTitle>
                  <Briefcase className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{activeJobsCount}</div>
                </CardContent>
              </Card>

              {/* Pending Evaluations */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Avaliações Pendentes
                  </CardTitle>
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{pendingEvaluations}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    currículos aguardando análise
                  </p>
                </CardContent>
              </Card>

              {/* Credits */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Créditos Disponíveis
                  </CardTitle>
                  <Wallet className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{credits}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    para publicação de vagas
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Chart Card - Full width below */}
            <Card>
              <CardHeader className="pb-2 px-3 md:px-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Candidaturas
                  </CardTitle>
                  <ToggleGroup
                    type="single"
                    value={period}
                    onValueChange={(v) => v && setPeriod(v)}
                    size="sm"
                  >
                    <ToggleGroupItem value="30" className="text-xs px-2">
                      30d
                    </ToggleGroupItem>
                    <ToggleGroupItem value="7" className="text-xs px-2">
                      7d
                    </ToggleGroupItem>
                    <ToggleGroupItem value="0" className="text-xs px-2">
                      Hoje
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
              </CardHeader>
              <CardContent className="px-4 md:px-6">
                <div className="h-52 md:h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <defs>
                        <linearGradient id="colorCandidaturas" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 10 }}
                        className="text-muted-foreground"
                        interval="preserveStartEnd"
                      />
                      <YAxis 
                        tick={{ fontSize: 10 }} 
                        className="text-muted-foreground" 
                        width={30}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="candidaturas"
                        stroke="hsl(var(--primary))"
                        fill="url(#colorCandidaturas)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Filters */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar vaga por título..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="pl-9"
                    />
                  </div>
                  <Select value={filterStatus} onValueChange={(v) => {
                    setFilterStatus(v);
                    setCurrentPage(1);
                  }}>
                    <SelectTrigger className="w-full md:w-40">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os status</SelectItem>
                      <SelectItem value="ativa">🟢 Ativas</SelectItem>
                      <SelectItem value="pausada">🟡 Pausadas</SelectItem>
                      <SelectItem value="preenchida">✅ Preenchidas</SelectItem>
                      <SelectItem value="finalizada">🔴 Finalizadas</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterType} onValueChange={(v) => {
                    setFilterType(v);
                    setCurrentPage(1);
                  }}>
                    <SelectTrigger className="w-full md:w-40">
                      <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os tipos</SelectItem>
                      <SelectItem value="presencial">Presencial</SelectItem>
                      <SelectItem value="híbrido">Híbrido</SelectItem>
                      <SelectItem value="remoto">Remoto</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterCity} onValueChange={(v) => {
                    setFilterCity(v);
                    setCurrentPage(1);
                  }}>
                    <SelectTrigger className="w-full md:w-40">
                      <SelectValue placeholder="Cidade" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas as cidades</SelectItem>
                      {cities.map((city) => (
                        <SelectItem key={city} value={city!}>
                          {city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Jobs List */}
            <div className="space-y-3">
              <h2 className="text-lg font-semibold">
                Vagas {filterStatus === "all" ? "" : filterStatus === "ativa" ? "Ativas" : filterStatus === "pausada" ? "Pausadas" : filterStatus === "preenchida" ? "Preenchidas" : "Finalizadas"} ({totalJobsCount})
                {totalJobsCount > JOBS_PER_PAGE && (
                  <span className="text-sm font-normal text-muted-foreground ml-2">
                    - Página {currentPage} de {totalPages}
                  </span>
                )}
              </h2>
              
              {jobsLoading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <Card key={i}>
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          <Skeleton className="h-6 w-3/4" />
                          <Skeleton className="h-4 w-1/2" />
                          <div className="flex justify-between">
                            <Skeleton className="h-8 w-20" />
                            <Skeleton className="h-8 w-24" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : paginatedJobs.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-muted-foreground">
                    <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhuma vaga encontrada com os filtros selecionados.</p>
                  </CardContent>
                </Card>
              ) : (
                paginatedJobs.map((job) => (
                  <Card 
                    key={job.id} 
                    className="hover:shadow-md transition-shadow cursor-pointer hover:border-primary"
                    onClick={() => navigate(`/empresa/vaga/${job.id}/candidatos`)}
                  >
                    <CardContent className="p-4">
                      {/* Mobile Layout */}
                      <div className="md:hidden space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold text-base leading-tight">{job.title}</h3>
                          <Badge 
                            variant={
                              job.status === 'ativa' ? 'default' : 
                              job.status === 'pausada' ? 'secondary' :
                              job.status === 'preenchida' ? 'default' : 'outline'
                            }
                            className={`text-xs shrink-0 ${job.status === 'preenchida' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                          >
                            {job.status === 'ativa' ? '🟢 Ativa' : 
                             job.status === 'pausada' ? '🟡 Pausada' : 
                             job.status === 'preenchida' ? '✅ Preenchida' : '🔴 Finalizada'}
                          </Badge>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                          {job.city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3.5 w-3.5" />
                              {job.city}{job.state && `, ${job.state}`}
                            </span>
                          )}
                          <Badge variant="secondary" className="text-xs hidden md:inline-flex">
                            {job.job_type.map(t => jobTypeLabels[t] || t).join(", ")}
                          </Badge>
                        </div>

                        <div className="flex items-center justify-between pt-1 border-t">
                          {(job.status !== 'finalizada' && job.status !== 'preenchida') && (
                            <div>
                              <p className="text-2xl font-bold">{job.applications_count}</p>
                              <p className="text-xs text-muted-foreground">candidatos</p>
                            </div>
                          )}

                          {(job.status === 'finalizada' || job.status === 'preenchida') ? (
                            <div className="flex items-center gap-2 ml-auto">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRepostJob(job);
                                }}
                                disabled={reposting === job.id}
                                className="gap-2"
                              >
                                {reposting === job.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-4 w-4" />
                                )}
                                Repostar
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/empresa/vaga/${job.id}/editar`);
                                }}
                                className="gap-2"
                              >
                                <Edit className="h-4 w-4" />
                                Editar
                              </Button>
                            </div>
                          ) : (
                            <div className="flex flex-col items-end gap-2 ml-auto">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/empresa/vaga/${job.id}/editar`);
                                }}
                                className="gap-2"
                              >
                                <Edit className="h-4 w-4" />
                                Editar
                              </Button>
                              {job.status === 'ativa' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkAsFilled(job.id);
                                  }}
                                  className="gap-2 text-green-600 border-green-600 hover:bg-green-50 dark:hover:bg-green-900/30"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                  Preenchida
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Desktop Layout */}
                      <div className="hidden md:flex md:items-center justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg">{job.title}</h3>
                            <Badge 
                              variant={
                                job.status === 'ativa' ? 'default' : 
                                job.status === 'pausada' ? 'secondary' :
                                job.status === 'preenchida' ? 'default' : 'outline'
                              }
                              className={`text-xs ${job.status === 'preenchida' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                            >
                              {job.status === 'ativa' ? '🟢 Ativa' : 
                               job.status === 'pausada' ? '🟡 Pausada' : 
                               job.status === 'preenchida' ? '✅ Preenchida' : '🔴 Finalizada'}
                            </Badge>
                          </div>
                          {job.city && (
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <MapPin className="h-3.5 w-3.5" />
                              {job.city}{job.state && `, ${job.state}`}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-4">
                          {(job.status === 'finalizada' || job.status === 'preenchida') ? (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRepostJob(job);
                                }}
                                disabled={reposting === job.id}
                                className="gap-2"
                              >
                                {reposting === job.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-4 w-4" />
                                )}
                                Repostar
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/empresa/vaga/${job.id}/editar`);
                                }}
                                className="text-muted-foreground hover:text-foreground"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <div className="flex flex-col items-end gap-2">
                              <div className="flex items-center gap-3">
                                <div className="text-right">
                                  <p className="text-2xl font-bold">{job.applications_count}</p>
                                  <p className="text-xs text-muted-foreground">candidatos</p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/empresa/vaga/${job.id}/editar`);
                                  }}
                                  className="text-muted-foreground hover:text-foreground"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </div>
                              {job.status === 'ativa' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkAsFilled(job.id);
                                  }}
                                  className="gap-2 text-green-600 border-green-600 hover:bg-green-50 dark:hover:bg-green-900/30"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                  Preenchida
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}

              {/* Pagination */}
              {totalJobsCount > 0 && (
                <Pagination className="mt-6">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                        className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                    
                    {generatePageNumbers().map((pageNum, idx) => (
                      pageNum === "ellipsis" ? (
                        <PaginationItem key={`ellipsis-${idx}`}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      ) : (
                        <PaginationItem key={pageNum}>
                          <PaginationLink
                            onClick={() => handlePageChange(pageNum)}
                            isActive={currentPage === pageNum}
                            className="cursor-pointer"
                          >
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                      )
                    ))}
                    
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                        className={currentPage === totalPages || totalPages === 0 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </div>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default EmpresaDashboardPage;
